import { Chip } from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid';
import { cn } from 'cn';
import ms from 'ms';

import { type ReprocessingInvoice } from '~/data/invoice-reprocessing';

import { type columns } from '~/modules/supplier-connections/data-exchange-companies/columnDefinitions';

import { getFormattedNumber, truncate } from '@vestigas/utils';
import {
  dateFull,
  datetimeLocalizedDefault,
  formatDate,
  i18n,
  parseDate,
} from '~/utils';

import { InvoiceCheckWatermark, InvoiceReprocessingStatus } from '../../common';
import { REPROCESSING_INVOICE_CHANNEL_LABELS } from '../../constants';
import { getStatusColorClass } from '../../utils';

const headerClassName = 'h-6 text-sm';

export const COLUMN_DEFINITIONS: Array<GridColDef<ReprocessingInvoice>> = [
  {
    align: 'center',
    field: 'status',
    headerClassName,
    headerName: i18n.t(($) => $.invoiceReprocessingList.column.status, {
      ns: 'invoiceReprocessing',
    }),
    renderCell({ row }) {
      return (
        <div className="flex size-full items-center justify-center">
          <Chip
            className={cn('w-full', getStatusColorClass(row.status))}
            label={
              <InvoiceReprocessingStatus
                processId={row.id}
                refetchInterval={ms('20s')}
                status={row.status}
              />
            }
          />
        </div>
      );
    },
    width: 220,
  },
  {
    field: 'watermarkType',
    headerClassName,
    headerName: i18n.t(($) => $.invoiceReprocessingList.column.watermarkType, {
      ns: 'invoiceReprocessing',
    }),
    renderCell({ row }) {
      return (
        <div className="flex size-full items-center justify-center">
          <InvoiceCheckWatermark watermarkType={row.watermarkType} />
        </div>
      );
    },
    width: 96,
  },
  {
    align: 'right',
    field: 'createdOn',
    headerClassName,
    headerName: i18n.t(($) => $.invoiceReprocessingList.column.createdOn, {
      ns: 'invoiceReprocessing',
    }),
    valueFormatter(value) {
      return formatDate(value, datetimeLocalizedDefault);
    },
    valueGetter(value) {
      return value ? parseDate(value) : undefined;
    },
    width: 150,
  },
  {
    align: 'right',
    field: 'invoiceDate',
    headerClassName,
    headerName: i18n.t(($) => $.invoiceReprocessingList.column.invoiceDate, {
      ns: 'invoiceReprocessing',
    }),
    valueFormatter(value) {
      return value ? formatDate(value, dateFull) : '';
    },
    valueGetter(value) {
      return value ? parseDate(value) : undefined;
    },
    width: 150,
  },
  {
    field: 'channel',
    headerClassName,
    headerName: i18n.t(($) => $.invoiceReprocessingList.column.channel, {
      ns: 'invoiceReprocessing',
    }),
    valueFormatter(value) {
      return REPROCESSING_INVOICE_CHANNEL_LABELS[value ?? ''];
    },
    width: 130,
  },
  {
    align: 'center',
    field: 'dryRun',
    headerClassName,
    headerName: i18n.t(($) => $.invoiceReprocessingList.column.dryRun, {
      ns: 'invoiceReprocessing',
    }),
    valueFormatter(value) {
      return value
        ? i18n.t(($) => $.yes, { ns: 'common' })
        : i18n.t(($) => $.no, { ns: 'common' });
    },
    width: 120,
  },
  {
    align: 'center',
    field: 'needsAiParsing',
    headerClassName,
    headerName: i18n.t(($) => $.invoiceReprocessingList.column.needsAiParsing, {
      ns: 'invoiceReprocessing',
    }),
    valueFormatter(value) {
      return value
        ? i18n.t(($) => $.yes, { ns: 'common' })
        : i18n.t(($) => $.no, { ns: 'common' });
    },
    width: 120,
  },
  {
    field: 'invoiceNumber',
    headerClassName,
    headerName: i18n.t(($) => $.invoiceReprocessingList.column.invoiceNumber, {
      ns: 'invoiceReprocessing',
    }),
    width: 180,
  },
  {
    align: 'right',
    field: 'totalAmount',
    headerClassName,
    headerName: i18n.t(($) => $.invoiceReprocessingList.column.totalAmount, {
      ns: 'invoiceReprocessing',
    }),
    valueFormatter(value: number | undefined) {
      return getFormattedNumber<string>({ value, digits: 2 });
    },
    width: 140,
  },
  {
    field: 'currency',
    headerClassName,
    headerName: i18n.t(($) => $.invoiceReprocessingList.column.currency, {
      ns: 'invoiceReprocessing',
    }),
    width: 80,
  },
  {
    field: 'buyerName',
    headerClassName,
    headerName: i18n.t(($) => $.invoiceReprocessingList.column.buyerName, {
      ns: 'invoiceReprocessing',
    }),
    width: 240,
  },
  {
    field: 'sellerName',
    headerClassName,
    headerName: i18n.t(($) => $.invoiceReprocessingList.column.sellerName, {
      ns: 'invoiceReprocessing',
    }),
    width: 240,
  },
  {
    field: 'emailSender',
    headerClassName,
    headerName: i18n.t(($) => $.invoiceReprocessingList.column.emailSender, {
      ns: 'invoiceReprocessing',
    }),
    width: 240,
  },
  {
    field: 'emailSubject',
    headerClassName,
    headerName: i18n.t(($) => $.invoiceReprocessingList.column.emailSubject, {
      ns: 'invoiceReprocessing',
    }),
    width: 240,
  },
  {
    align: 'right',
    field: 'emailReceivedDatetime',
    headerClassName,
    headerName: i18n.t(
      ($) => $.invoiceReprocessingList.column.emailReceivedDatetime,
      {
        ns: 'invoiceReprocessing',
      },
    ),
    valueFormatter(value) {
      return value ? formatDate(value, datetimeLocalizedDefault) : '';
    },
    valueGetter(value) {
      return value ? parseDate(value) : undefined;
    },
    width: 150,
  },
  {
    align: 'right',
    field: 'processFinishedOn',
    headerClassName,
    headerName: i18n.t(
      ($) => $.invoiceReprocessingList.column.processFinishedOn,
      {
        ns: 'invoiceReprocessing',
      },
    ),
    valueFormatter(value) {
      return value ? formatDate(value, datetimeLocalizedDefault) : '';
    },
    valueGetter(value) {
      return value ? parseDate(value) : undefined;
    },
    width: 150,
  },
  {
    field: 'recipientMatchedCompanyName',
    headerClassName,
    headerName: i18n.t(
      ($) => $.invoiceReprocessingList.column.recipientMatchedCompanyName,
      { ns: 'invoiceReprocessing' },
    ),
    width: 240,
  },
  {
    field: 'recipientMatchedCompanyId',
    headerClassName,
    headerName: i18n.t(
      ($) => $.invoiceReprocessingList.column.recipientMatchedCompanyId,
      { ns: 'invoiceReprocessing' },
    ),
    valueFormatter(value) {
      return truncate(value, 6, 3);
    },
    width: 170,
  },
  {
    field: 'id',
    headerClassName,
    headerName: i18n.t(($) => $.invoiceReprocessingList.column.id, {
      ns: 'invoiceReprocessing',
    }),
    valueFormatter(value) {
      return truncate(value, 6, 3);
    },
    width: 100,
  },
  {
    field: 'statusDetails',
    headerClassName,
    headerName: i18n.t(($) => $.invoiceReprocessingList.column.statusDetails, {
      ns: 'invoiceReprocessing',
    }),
    valueGetter(value) {
      return JSON.stringify(value);
    },
    width: 240,
  },
  {
    field: 'comment',
    headerClassName,
    headerName: i18n.t(($) => $.invoiceReprocessingList.column.comment, {
      ns: 'invoiceReprocessing',
    }),
    width: 240,
  },
] as const;

export type InvoiceReprocessingListColumns = keyof typeof columns;
